import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { RootState } from 'StoreModel';
import styled from 'styled-components';
import { ApplicationDefinition, ApplicationEntry } from 'redux/types/account';
import { useHistory } from 'react-router-dom';
import { CoverComponent } from 'layout/common';
import { StyledLogo } from 'components/community/sidebar';
import moment from 'moment';
import DisabledCkeditorDescription from './disabled-ckeditor-description';
import { StyledButton } from 'primitives/Button/style';
import { ApplicationStatus } from 'redux/types/enums';
import SignupModal from 'components/common/modal/SignupModal';
import { overrideTranslation } from 'services/literals/overrides';

const ApplicationContainer = styled.div`
  flex: 1;
  max-width: 45em;
  width: 100%;
  align-self: center;

  @media (max-width: ${({ theme }) => theme.sizes.mobileWidth}) {
    width: 100%;
    padding: 0;
  }
`;

const Root = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
`;

const Container = styled.div`
  position: relative;
  top: 2em;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 2em;

  width: 100%;

  padding-left: 14em;

  @media (max-width: ${({ theme }) => theme.sizes.mobileWidth}) {
    flex-direction: column;
    gap: 2em;

    padding-left: 1em;
  }
`;

const Sidebar = styled.div`
  position: sticky;
  top: 0;

  display: flex;
  flex-direction: column;
  gap: 1em;

  width: 100%;
  max-width: 15em;
  height: 100%;

  padding-top: 2em;
  margin-right: 4em;

  text-align: left;

  @media (max-width: ${({ theme }) => theme.sizes.mobileWidth}) {
    max-width: 100%;

    padding: 0.5em 1em;
  }
`;

const ApplyLabel = styled.span`
  color: ${({ theme }) => theme.colors.black};
  font-weight: 600;
  font-size: 1.5em;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;

  width: 100%;
`;

const LoginInfoLabel = styled.span`
  color: ${({ theme }) => theme.colors.grey.thirdMedium};
  line-height: 1.5em;
`;

export const FillApplicationButton = styled(StyledButton)`
  &&& {
    flex: 1;
    padding: 0.6em 2em;
    font-size: 0.7em;

    @media (max-width: ${({ theme }) => theme.sizes.mobileWidth}) {
      padding: 1em 2em;
      font-size: 0.9em;
    }
  }
`;

const mapStateToProps = (state: RootState) => ({
  literals: state.literals,
  applications: state.account.applications,
  bearer: state.account.session.session.bearer,
});

const mapDispatchToProps = {};
type dispatchType = typeof mapDispatchToProps;

interface Props extends ReturnType<typeof mapStateToProps>, dispatchType {
  applicationDefinition: ApplicationDefinition;
}

const NewProjectApplication: React.FC<Props> = props => {
  const { applicationDefinition, literals, applications, bearer } = props;
  const [showSignupModal, setShowSignupModal] = useState<boolean>(false);
  const [inSignUp, setInSignUp] = useState<boolean>(false);
  const history = useHistory();
  const handleClick = () => {
    if (bearer && applications && applicationDefinition) {
      const acceptedOrSubmittedApplication = applications.find((application: ApplicationEntry) => {
        return (
          application.applicationDefinitionId === applicationDefinition.id &&
          (application.status === ApplicationStatus.Draft || application.status === ApplicationStatus.Apply)
        );
      });

      if (acceptedOrSubmittedApplication) {
        history.push(
          `/application-definition/${applicationDefinition.id}/application/${acceptedOrSubmittedApplication.id}`,
        );
      } else {
        history.push(`/application-definition/${applicationDefinition.id}/new-application`);
      }
    } else {
      setInSignUp(true);
      setShowSignupModal(true);
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('token') || inSignUp) {
      handleClick();
      setInSignUp(false);
    }
  }, [bearer]);

  const handleExistingUserAction = () => {
    history.push(`/application-definition/${applicationDefinition.id}/new-application`);
  };
  return (
    <Root>
      <CoverComponent
        style={{ padding: '0' }}
        communityId={applicationDefinition.communityId}
        applicationDefinitionId={applicationDefinition.id}
      />
      <StyledLogo
        style={{ position: 'absolute', top: '12.5em', left: '2em' }}
        src={applicationDefinition.communityLogo || ''}
      />
      <Container>
        <ApplicationContainer>
          <DisabledCkeditorDescription description={applicationDefinition.description} />
        </ApplicationContainer>
        <Sidebar>
          <ApplyLabel>{overrideTranslation('application_apply_now') || literals.application_apply_now}</ApplyLabel>
          <Row>
            {moment(applicationDefinition.endDate).isAfter(moment()) ? (
              <>
                {!bearer && (
                  <LoginInfoLabel>
                    {overrideTranslation('create_account_for_application_label') ||
                      literals.create_account_for_application_label}
                  </LoginInfoLabel>
                )}
                <FillApplicationButton onClick={handleClick}>
                  {overrideTranslation('application_fill_your_application') ||
                    literals.application_fill_your_application}
                </FillApplicationButton>
              </>
            ) : (
              <span>{literals.application_closed_period}</span>
            )}
          </Row>
        </Sidebar>
      </Container>
      {showSignupModal && !bearer && (
        <SignupModal
          open={showSignupModal}
          existingUserAction={handleExistingUserAction}
          onClose={() => setShowSignupModal(false)}
        />
      )}
    </Root>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(NewProjectApplication);
